import { authSession, isAppError, isForbidden, type AppError } from '@/shared/api'

/**
 * A 403 saying the account is blocked or deleted is not a permission answer
 * about one action: the credential is valid and every request will fail the
 * same way. Refreshing cannot help, so it is a hard sign-out.
 *
 * Matching on server prose is brittle. It is the only option until the backend
 * returns a stable code for this case — and it is a backstop, not the main
 * path: api-service never reads the account row (it only verifies the
 * signature), so in practice a blocked account is caught by the next refresh,
 * which goauth answers 403 and `refreshCredential` turns into a lost session.
 */
const DISABLED_MESSAGES = ['account is blocked', 'account is deleted']

export const isDisabledAccount = (error: AppError) =>
  isForbidden(error) &&
  DISABLED_MESSAGES.some((message) => error.message.toLowerCase().includes(message))

/**
 * Wired into the query and mutation caches, which is the one place that sees
 * every request in the app rather than just the session one.
 */
export function reportRequestError(error: unknown): void {
  if (!isAppError(error)) return
  // `sessionLost`, not a bare `signOut`: the handler wired in `auth-bridge`
  // signs out *and* navigates to /login. A sign-out alone would leave the user
  // on a dead screen whose refetches all fail the same way.
  if (isDisabledAccount(error)) authSession.sessionLost()
}
